import {Component, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {interval, Subscription} from 'rxjs';

import {GatewayService} from '@services/administration/gateway/gateway.service';
import {NotificationService} from '@services/notification/notification.service';

import {Gateway} from '@models/adminstration/gateway/gateway';
import {Connector} from '@models/adminstration/gateway/connector';

import {IbaConfigurationModalComponent} from '@components/administration/gateway/gateway-configuration/gateway-service/iba-configuration-modal/iba-configuration-modal.component';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-gateway-service',
    templateUrl: './gateway-service.component.html',
    styleUrls: ['./gateway-service.component.scss'],
    standalone: false
})
export class GatewayServiceComponent implements OnInit {

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  isAdminRole = false;

  @Input()
  isTechAdminRole = false;

  @Input()
  isSuperAdminRole = false;

  connectors: Connector[] = [];
  isSuperAdminOrAdminOrTechAdmin = false;
  private POLL_TIMEOUT = interval(20000);
  private MAX_POLL_COUNT = 0;
  private COMPONENT_NAME: string = 'GatewayServiceComponent';
  private IBA_CONNECTOR_NAME = 'ibaConnector';
  private UPDATE_DAEMON = 'updateDaemon';

  constructor(
      private gatewayService: GatewayService,
      private notification: NotificationService,
      private translate: TranslateService,
      private modalService: ModalService,
      private arrayOperationService: ArrayOperationService) {
  }

  ngOnInit(): void {
    this.loadConnectorsData(this.gateway.connectors);
    this.isSuperAdminOrAdminOrTechAdmin = this.isAdminRole || this.isTechAdminRole || this.isSuperAdminRole;
  }

  spin(connector: Connector): boolean {
    return connector.shouldEnabled !== null && connector.shouldEnabled !== connector.enabled;
  }

  public getStatusClass(connector: Connector): string {
    if (!connector.run) {
      return 'status-danger';
    }
    if (connector.run && connector.enabled) {
      return 'status-success';
    }
    if (connector.run && !connector.enabled) {
      return 'status-warning';
    }
    return '';
  }

  buttonIsDisabled(connector: Connector): boolean {
    return (!this.isSuperAdminOrAdminOrTechAdmin) ||
        ((connector.name === this.UPDATE_DAEMON) || connector.shouldEnabled != null && (connector.shouldEnabled != connector.enabled));
  }

  onToggleConnectorStatus(connector: Connector) {
    connector.shouldEnabled = !connector.enabled;
    this.gatewayService.toggleConnectorStatus(this.gateway.id, connector).subscribe((updatedConnector: Connector) => {
      this.updateConnector(connector.id, updatedConnector);
      this.getConnectorStatus(connector.id);
    }, (error) => {
      this.notification.error(this.translate.instant('Error activating gateway connector'), error, this.COMPONENT_NAME, true);
    });
  }

  displayIbaConfigButton(connector: Connector): boolean {
    return connector.name === this.IBA_CONNECTOR_NAME;
  }

  openIbaConfig() {
    const modalRef = this.modalService.open(IbaConfigurationModalComponent, {size: 'lg'});
    modalRef.componentInstance.gatewayId = this.gateway.id;
    modalRef.componentInstance.isAdminRole = this.isAdminRole;
    modalRef.componentInstance.isTechAdminRole = this.isTechAdminRole;
    modalRef.componentInstance.isSuperAdminRole = this.isSuperAdminRole;
    modalRef.result.then();
  }

  private getConnectorStatus(connectorId: number) {
    const subscription: Subscription = this.POLL_TIMEOUT.subscribe(() => {
      this.gatewayService.getConnectorStatus(this.gateway.id, connectorId).subscribe((updatedConnector: Connector) => {
        const connectorIndex = this.arrayOperationService.getItemIndex(this.connectors, 'id', connectorId);
        if (connectorIndex >= 0) {
          this.connectors[connectorIndex] = updatedConnector;
        }
        if (connectorIndex >= 0 && !(this.connectors[connectorIndex].shouldEnabled != null &&
            (this.connectors[connectorIndex].shouldEnabled != this.connectors[connectorIndex].enabled))) {
          subscription.unsubscribe();
        } else {
          this.MAX_POLL_COUNT++;
          if (this.MAX_POLL_COUNT > 10) {
            subscription.unsubscribe();
          }
        }
      }, (error) => {
        console.log('Error getting connector status.', error);
        subscription.unsubscribe();
      });
    });
  }

  private loadConnectorsData(sig: any): void {
    this.connectors = [];
    if(!sig){
      return;
    }
    const objectKeys = Object.keys(sig);
    objectKeys.forEach(element => {
      this.connectors.push(sig[element]);
    });
  }

  private updateConnector(connectorId: number, updatedConnector: Connector): void {
    const connectorIndex = this.arrayOperationService.getItemIndex(this.connectors, 'id', connectorId);
    if (connectorIndex >= 0) {
      this.connectors[connectorIndex] = updatedConnector;
    }
  }

}
